import { singleValueMatches, commutedCalculationMatches } from "../../../utils/answerMatchUtils";

const cleanBlankValue = (v) => {
  if (v == null) return "";
  const decoded = String(v)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/<[^>]*>/g, "");
  return decoded.replace(/ /g, " ").trim();
};

/**
 * Every value the server would accept for one blank.
 *
 * `correct_answer` is a flattened DISPLAY array — one string per blank, alternatives already
 * collapsed. `answer_data.correct_blanks[i]` is the real accepting set the server graded against
 * (each slot may itself be a list of alternatives), so prefer it and fall back to the display value.
 */
export const acceptedValuesForBlank = (question, blankIndex, displayValue) => {
  const slot = question?.answer_data?.correct_blanks?.[blankIndex];
  const fromSlot = (Array.isArray(slot) ? slot : [slot])
    .map(cleanBlankValue)
    .filter((v) => v !== "");
  if (fromSlot.length > 0) return fromSlot;
  const fallback = cleanBlankValue(displayValue);
  return fallback === "" ? [] : [fallback];
};

/**
 * Did this blank earn its mark? Matches against the whole accepting set rather than the single
 * display string, so a blank the server credited via an alternative is not painted red beside a
 * verdict chip that says the question was correct.
 */
/**
 * `rows_interchangeable` on an image-fill table: the ROWS may come in any order, but each row is one
 * answer and the columns stay bound. Boxes are grouped into rows by their height on the picture, and
 * each submitted row is matched to an unused expected row with the same number of boxes. Returns one
 * verdict per box, or null when the question is not a row-pooled overlay. Pooling every box on its own
 * painted a whole "list all the combinations" table green — the same row written twice included —
 * because each letter appears somewhere in the table (NLIGSC G1 2016 mock Q40a).
 */
export const rowPooledBlankVerdicts = (question, submittedList) => {
  const blanks = question?.answer_data?.overlay_layout?.blanks;
  if (!question?.answer_data?.overlay_layout?.rows_interchangeable || !Array.isArray(blanks) || blanks.length < 2
    || !Array.isArray(submittedList)) return null;
  const order = blanks.map((b, i) => i).sort((a, b) => (Number(blanks[a]?.y) || 0) - (Number(blanks[b]?.y) || 0));
  const rows = [];
  for (const i of order) {
    const y = Number(blanks[i]?.y) || 0;
    const tol = (Number(blanks[i]?.h) || 0.05) / 2;
    const row = rows.find((r) => Math.abs(r.y - y) <= tol);
    if (row) row.idx.push(i);
    else rows.push({ y, idx: [i] });
  }
  rows.forEach((r) => r.idx.sort((a, b) => (Number(blanks[a]?.x) || 0) - (Number(blanks[b]?.x) || 0)));
  const used = new Set();
  const verdicts = blanks.map(() => false);
  for (const r of rows) {
    const sub = r.idx.map((i) => String(submittedList[i] ?? "").trim());
    if (sub.every((v) => v === "")) continue;
    const hit = rows.findIndex((e, k) => !used.has(k) && e.idx.length === r.idx.length
      && e.idx.every((j, col) => acceptedValuesForBlank(question, j, null).some((c) => singleValueMatches(sub[col], c))));
    if (hit === -1) continue;
    used.add(hit);
    r.idx.forEach((i) => { verdicts[i] = true; });
  }
  return verdicts;
};

/**
 * A cell holding a comma list ("trapezium, parallelogram", "16, 22") is a set: the server grades it
 * order-free (blankAcceptIsValueSet), so "parallelogram,trapezium" was credited but painted red
 * (NLIGSC G1 2014 mock Q27, HABS 2008 Q21). Bracketed values are coordinates, where order matters.
 */
export const commaListMatches = (student, accept) => {
  const s = cleanBlankValue(student);
  const c = cleanBlankValue(accept);
  if (!c.includes(",") || /[()[\]]/.test(c) || /[()[\]]/.test(s)) return false;
  const split = (v) => v.split(",").map((p) => p.trim()).filter(Boolean);
  const want = split(c);
  const got = split(s);
  if (want.length < 2 || got.length !== want.length) return false;
  const used = new Set();
  return want.every((w) => {
    const i = got.findIndex((g, k) => !used.has(k) && singleValueMatches(g, w));
    if (i === -1) return false;
    used.add(i);
    return true;
  });
};

export const blankIsCorrect = (question, blankIndex, studentValue, displayValue, { pooled = false, displayList = null, submittedList = null } = {}) => {
  const student = String(studentValue ?? "").trim();
  if (student === "") return false;
  const byRow = rowPooledBlankVerdicts(question, submittedList);
  if (byRow) return !!byRow[blankIndex];
  // Pooled/interchangeable blanks accept any of the question's values in any box.
  const candidates = pooled && Array.isArray(displayList)
    ? displayList.flatMap((_, i) => acceptedValuesForBlank(question, i, displayList[i]))
    : acceptedValuesForBlank(question, blankIndex, displayValue);
  // "5×9=45" for "9 × 5 = 45" is the same calculation and the server credits it (St Paul's Sample P2
  // 2025 mock Q13) — without this a box the server marked right was painted red on a partial answer.
  return candidates.some((c) => singleValueMatches(student, c) || commutedCalculationMatches(student, c)
    || commaListMatches(student, c));
};
